/**
 * StageDonut — current-stage distribution as a donut plus a legend.
 *
 * Hand-rolled SVG rather than a charting library: the repo has no chart
 * dependency, and adding one to draw four widgets would be a large surface for a
 * small need. Geometry rides in `d` / `viewBox` ATTRIBUTES, never in a `style`
 * prop, so the design system's no-inline-styles rule holds even though the paths
 * are computed.
 *
 * The whole chart is `aria-hidden` and the numbers are ALSO in the legend as
 * text, so a screen reader gets the data rather than an unlabelled arc soup. The
 * legend is the accessible representation, not a decoration — which is also why
 * the hover tooltip needs no keyboard equivalent: it names an arc that carries
 * no text, and every figure it shows is already sitting in the list below it.
 *
 * RING ABOVE, LEGEND BENEATH IT — the same stack `FunnelBars` uses, and this
 * widget is the one that moved. Jared, 2026-08-27: *"can we make the charts
 * roughly the same size? It looks kind of silly with the current stage being
 * this little circle and the funnel being this big funnel… let's just put the
 * legends in the same place on both… the big chart in the middle and then the
 * bottom left is the legend."* He named the funnel as the reference — *"I think
 * the one on the right looks good, so maybe just keep it like that"* — so the
 * funnel kept its shape and the donut came to it.
 *
 * The size difference was structural, not a style choice: the ring was a fixed
 * 220×220 pinned beside its legend, so it could never fill a panel the funnel
 * fills at `w-full`. Dropping the fixed width/height and capping the ring
 * instead lets both widgets grow with the column they sit in.
 *
 * The tooltip's SIDE comes from the segment's own mid-angle rather than from the
 * pointer's position, because a donut has no left and right half a pointer could
 * be in — it has arcs. A segment centred in the right half of the ring pins the
 * bubble left and vice versa, so the bubble never sits on the arc it describes.
 *
 * THE NOT-A-CANDIDATE FILTER LIVES INSIDE THIS COMPONENT, and that is
 * deliberate. It began at the call site, which broke the invariant
 * `export/ReportDocument.tsx` is built on — that file renders THESE components
 * so "the export mirrors the dashboard" is true by construction rather than by
 * anybody remembering. A filter applied by one caller and not the other made
 * the downloaded `.html` disagree with the screen it claims to mirror, and no
 * test failed. Moving the rule in here restores the guarantee: every caller
 * gets it, including the next one.
 *
 * The rule itself, and why `Source` is an exception to the ladder, is in
 * `seriesDefaults.ts`. Callers pass the wire value unfiltered.
 *
 * SOME ARCS ARE HATCHED. This is the widget that can draw the most stages at
 * once — every bucket a search has, up to thirteen non-neutral names against an
 * eight-hue scale — so two arcs can legitimately share a hue and the tier
 * `chartPalette` assigns is what separates them. On a line that tier is a dash;
 * on a filled arc it is a diagonal hatch (`StageHatch.tsx`). The legend dot
 * carries the same hatch, or it would key two arcs to one dot.
 */

import type { SearchReportBucket } from "@nu-assist/contracts";

import { stageSeriesStyle } from "../../../../components/stages/chartPalette";
import { ChartTooltip } from "./ChartTooltip";
import { SeriesSwatch } from "./SeriesSwatch";
import { StageHatchDefs, StageSwatchDot, hatchFill, useHatchScope } from "./StageHatch";
import {
  CHART_VIEW_WIDTH,
  DONUT_INNER_RADIUS,
  DONUT_OUTER_RADIUS,
  DONUT_VIEW_HEIGHT,
} from "./chartLayout";
import { candidateStageBuckets } from "./seriesDefaults";
import { useHoverPoint } from "./useHoverPoint";

export interface StageDonutProps {
  /**
   * The report's own `current_stage_distribution`, UNFILTERED.
   *
   * Pass the wire value. This component drops the not-a-candidate stages and
   * re-bases the shares itself — see the note above about why that lives here
   * rather than at the call site.
   */
  buckets: readonly SearchReportBucket[];
}

// GEOMETRY LIVES IN `chartLayout.ts`, not here, and not in `FunnelBars`.
//
// This widget and the funnel have to grow together, which means one viewBox
// width and a height in the funnel's proportion at the funnel's REAL maximum.
// Both numbers were got wrong in this PR — first a square box under a
// `max-w-[320px]` cap that kept the ring small on a wide screen, then a height
// pinned to an eight-rung funnel that the same PR made unreachable, which made
// the donut the taller widget by up to 2.03x. The shared module carries the
// derivation and a test that fails when the ladder or the row height moves.
const VIEW_W = CHART_VIEW_WIDTH;
const VIEW_H = DONUT_VIEW_HEIGHT;
const OUTER = DONUT_OUTER_RADIUS;
const INNER = DONUT_INNER_RADIUS;
const CENTER_X = VIEW_W / 2;
const CENTER_Y = VIEW_H / 2;

/** Cartesian point on a circle, with 0° at 12 o'clock. */
function point(radius: number, fraction: number): [number, number] {
  const angle = fraction * 2 * Math.PI - Math.PI / 2;
  return [CENTER_X + radius * Math.cos(angle), CENTER_Y + radius * Math.sin(angle)];
}

/**
 * One donut segment as an SVG path.
 *
 * A single segment covering the whole circle is drawn as two half-arcs: one
 * elliptical arc of exactly 360° is degenerate (start point == end point) and
 * renders as nothing, which would make a single-stage search show an empty ring.
 * That is the case every closed-out search hits.
 */
function segmentPath(startFraction: number, endFraction: number): string {
  const sweep = endFraction - startFraction;
  if (sweep >= 1) {
    const mid = startFraction + 0.5;
    return `${segmentPath(startFraction, mid)} ${segmentPath(mid, startFraction + 1)}`;
  }
  const [ox1, oy1] = point(OUTER, startFraction);
  const [ox2, oy2] = point(OUTER, endFraction);
  const [ix2, iy2] = point(INNER, endFraction);
  const [ix1, iy1] = point(INNER, startFraction);
  const largeArc = sweep > 0.5 ? 1 : 0;
  return [
    `M ${ox1} ${oy1}`,
    `A ${OUTER} ${OUTER} 0 ${largeArc} 1 ${ox2} ${oy2}`,
    `L ${ix2} ${iy2}`,
    `A ${INNER} ${INNER} 0 ${largeArc} 0 ${ix1} ${iy1}`,
    "Z",
  ].join(" ");
}

export function StageDonut({ buckets: wireBuckets }: StageDonutProps) {
  const hover = useHoverPoint();
  // THE DOMAIN FILTER RUNS HERE, not at the call site, and that placement is
  // the fix for a real miss rather than a preference. See the header note.
  const buckets = candidateStageBuckets(wireBuckets);
  // THE HOLE COUNTS WHAT THE RING DRAWS. It used to take a `total` prop, and
  // every call site passed `kpis.total_candidacies` — which stopped being this
  // widget's denominator the moment two stages left the ring. Deriving it here
  // means the number in the middle and the shares around it cannot disagree,
  // and there is no prop left for a caller to pass the wrong thing to.
  // ONE reduction, not two. `sum` (for the arc geometry) and `total` (for the
  // hole) were the same fold over the same array under two names — N4 in the
  // iter-2 review of PR #797.
  const total = buckets.reduce((acc, bucket) => acc + bucket.count, 0);
  // NO PALETTE PROP, and that is the fix rather than an omission. This used to
  // take a set-scoped assignment the page built over the union of the donut's
  // stages and the funnel's rungs — which meant the same stage came out one
  // colour here and another on the Search Progress tab, whose set is different
  // by construction, and a third in the downloaded report, which rendered this
  // component with no palette at all. `stageSeriesStyle` is a function of the
  // stage NAME, so there is nothing left to pass and nothing left to diverge.
  const scope = useHatchScope();

  if (total <= 0) {
    // TWO DIFFERENT EMPTIES, and telling them apart is the whole point. A
    // search with no candidates is one thing; a search whose candidates are ALL
    // at an excluded stage is another, and saying "none yet" there is false at
    // the exact moment the filter is withholding everything. Raised as N1 in
    // the iter-2 review of PR #797.
    const filteredEverything = wireBuckets.length > 0;
    return (
      <p data-testid="stage-donut-empty" className="text-body text-text-secondary">
        {filteredEverything
          ? "Every candidate on this search is currently at a stage this chart excludes — Source and Jury Member are not candidates on the search."
          : "No candidates on this search yet."}
      </p>
    );
  }

  let cursor = 0;
  const segments = buckets.map((bucket) => {
    const start = cursor;
    cursor += bucket.count / total;
    return { bucket, start, end: cursor, style: stageSeriesStyle(bucket.label) };
  });

  return (
    <div data-testid="stage-donut">
      {/* The tooltip's positioning context — the SVG's own box, so the bubble
          lands over the ring rather than over the legend beneath it. The ring
          is centred by the viewBox rather than by a `mx-auto` on a capped box:
          see the geometry note above for why the cap had to go. */}
      <div className="relative w-full">
        <svg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          role="presentation"
          aria-hidden="true"
          className="block w-full"
          onPointerLeave={hover.hide}
        >
          <StageHatchDefs scope={scope} />
          {segments.map(({ bucket, start, end, style }, index) => (
            <path
              key={bucket.label}
              d={segmentPath(start, end)}
              // The arc's own midpoint decides the side: `(start + end) / 2` under
              // 0.5 is the right half of the ring (0 is 12 o'clock, sweeping
              // clockwise), so the bubble goes left and stays off the arc.
              onPointerEnter={() => hover.show(index, (start + end) / 2 < 0.5 ? "left" : "right")}
              className={`${style.fillClass} stroke-surface ${
                hover.index === null || hover.index === index ? "" : "opacity-60"
              }`}
              strokeWidth={2}
            />
          ))}
          {/* THE SECOND CHANNEL, in its own pass AFTER every arc. Two stages
              can share a hue — eight of them against thirteen stage names —
              and on a filled arc the tier renders as a hatch rather than as a
              dash. Drawn as a separate layer rather than inline with each arc
              so the base paths keep one-path-per-bucket order, and
              `pointer-events-none` so the overlay never eats the hover the arc
              beneath it is listening for. */}
          <g className="pointer-events-none">
            {segments
              .filter(({ style }) => style.tier > 0)
              .map(({ bucket, start, end, style }) => (
                <path
                  key={bucket.label}
                  d={segmentPath(start, end)}
                  fill={hatchFill(scope, style.tier)}
                  className={
                    hover.index === null || buckets[hover.index]?.label === bucket.label
                      ? ""
                      : "opacity-60"
                  }
                />
              ))}
          </g>
          <text
            x={CENTER_X}
            y={CENTER_Y - 4}
            textAnchor="middle"
            dominantBaseline="middle"
            className="fill-text text-h1"
          >
            {total}
          </text>
          {/* "candidates", NOT "total". The word used to sit under
              `total_candidacies` and meant the whole search; it now sits under
              the drawn population, which is a smaller number. Two different
              figures 100px apart — this one and the KPI above the panel — must
              not share a label that implies they are the same thing. */}
          <text
            x={CENTER_X}
            y={CENTER_Y + 20}
            textAnchor="middle"
            dominantBaseline="middle"
            className="fill-text-secondary text-caption"
          >
            candidates
          </text>
        </svg>

        {hover.index !== null && buckets[hover.index] ? (
          <ChartTooltip
            testId="stage-donut-tooltip"
            title={buckets[hover.index].label}
            rows={[
              {
                key: "count",
                label: "Candidates",
                value: String(buckets[hover.index].count),
                swatch: (
                  <SeriesSwatch
                    strokeClass={stageSeriesStyle(buckets[hover.index].label).strokeClass}
                    dash={stageSeriesStyle(buckets[hover.index].label).dash}
                  />
                ),
              },
              { key: "pct", label: "Share", value: `${buckets[hover.index].pct}%` },
            ]}
            side={hover.side}
          />
        ) : null}
      </div>

      {/* Left-aligned UNDER the ring, not beside it — see the note at the top
          of this file. `mt-3` is the funnel key's spacing, copied so the two
          panels line up rather than merely resembling each other. */}
      <ul data-testid="stage-donut-legend" className="mt-3 flex flex-col gap-1">
        {buckets.map((bucket) => (
          <li
            key={bucket.label}
            className="flex items-center gap-2 text-caption text-text-secondary"
          >
            <StageSwatchDot
              bgClass={stageSeriesStyle(bucket.label).bgClass}
              tier={stageSeriesStyle(bucket.label).tier}
            />
            <span>{bucket.label}</span>
            <strong className="text-text">{bucket.count}</strong>
            <span>({bucket.pct}%)</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
